import React, { useEffect, useState } from 'react';
import Ruleta from './Ruleta';
import '../styles/ruleta.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';
const STORAGE_KEY = 'giroexpress_ruleta_access';

// =====================================================================
// BYPASS TEMPORAL DEL CANDADO.
// En producción DEBE quedar `false` (apagado): el gate vuelve a exigir
// access_token + jti one-shot, tal como fue diseñado.
// En desarrollo local se puede reactivar SIN tocar este archivo creando
// `/app/frontend/.env.local` con la variable:
//     REACT_APP_DEV_OPEN_ACCESS=true
// La lógica original del candado queda intacta y se activa
// automáticamente cuando esta bandera es `false`.
//
// DEFENSA EN PROFUNDIDAD: aunque la variable exista por error, en el
// dominio de producción (ruletagiroexpress.com) el bypass NUNCA se
// activa. El candado real es la única vía de acceso allí.
// =====================================================================
const _PROD_HOST = 'ruletagiroexpress.com';
const _isProdHost = (() => {
  try {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    return host === _PROD_HOST || host.endsWith(`.${_PROD_HOST}`);
  } catch (_) {
    return false;
  }
})();
const TEMP_OPEN_ACCESS = !_isProdHost && process.env.REACT_APP_DEV_OPEN_ACCESS === 'true';

/**
 * Bloqueo visual del login de la ruleta.
 * Permite acceso solo si:
 *   - URL trae ?access_token=... y backend lo valida (PASO 4.1+)
 *   - localStorage[STORAGE_KEY] === 'true' (sesión ya autorizada — persiste
 *     entre recreaciones de Chrome Custom Tab, recargas y reabrir el
 *     navegador en el mismo origen)
 *
 * El parámetro ?source=giroexpress_app YA NO concede acceso (PASO 4.3).
 *
 * Importante: solo controla el acceso visual al LOGIN. No autoriza spin,
 * premios, tickets ni XP — eso sigue protegido por el resto del backend.
 */

// Cache a nivel de módulo: evita que React Strict Mode (doble montado en dev)
// dispare DOS fetch al endpoint /api/access/validate, lo cual quemaría el token
// de un solo uso en la primera llamada y devolvería valid:false en la segunda.
// Como la promesa se comparte entre montados, ambos efectos resuelven con el
// mismo resultado y el token se consume exactamente UNA vez.
let _validatePromise = null;
let _validatedToken = null;

function validateAccessToken(token) {
  if (_validatedToken === token && _validatePromise) {
    return _validatePromise;
  }
  _validatedToken = token;
  const url = `${API_URL}/api/access/validate?token=${encodeURIComponent(token)}`;
  console.log('[ACCESS][TOKEN_PRESENT]', !!token);
  console.log('[ACCESS][VALIDATE_URL]', url);
  _validatePromise = fetch(url)
    .then(async (r) => {
      const text = await r.text();
      console.log('[ACCESS][VALIDATE_STATUS]', r.status);
      console.log('[ACCESS][VALIDATE_BODY]', text);
      try { return JSON.parse(text); } catch { return { ok: false, valid: false }; }
    })
    .catch((e) => {
      console.log('[ACCESS][VALIDATE_STATUS]', 'network_error');
      console.log('[ACCESS][VALIDATE_BODY]', String(e));
      return { ok: false, valid: false };
    });
  return _validatePromise;
}

export default function RuletaGate() {
  const params = new URLSearchParams(window.location.search);
  // El snippet inline de /public/index.html corre ANTES de React y
  // mueve el access_token de la URL a window.__GE_AT__, dejando la barra
  // de direcciones limpia. Aquí lo leemos preferentemente desde allí; si
  // no existe (por ejemplo, el snippet no llegó a correr), caemos al
  // query param tradicional. La lógica de validación NO cambia.
  const accessToken = (typeof window !== 'undefined' && window.__GE_AT__) || params.get('access_token');

  // 🔒 LIMPIEZA INMEDIATA: en cuanto detectamos un access_token en la URL,
  // lo borramos de la barra de direcciones ANTES de validarlo en el backend.
  // Razón: si el usuario está dentro del Chrome Custom Tab de Giro Expréss y
  // toca "Abrir en Google Chrome" (o copia la URL), Chrome / el portapapeles
  // recibirían el access_token completo. Al limpiar la URL en el primer
  // render, lo único que Chrome verá será https://ruletagiroexpress.com/.
  // El token queda solo en memoria de esta pestaña para el fetch a
  // /api/access/validate, y como es jti one-shot, no se puede reutilizar
  // en otra pestaña/navegador.
  if (accessToken && typeof window !== 'undefined') {
    try {
      const _u = new URL(window.location.href);
      _u.searchParams.delete('access_token');
      const _cleaned = _u.pathname + (_u.searchParams.toString() ? `?${_u.searchParams.toString()}` : '') + _u.hash;
      window.history.replaceState({}, '', _cleaned);
      console.log('[ACCESS][URL_TOKEN_STRIPPED]');
    } catch (_) { /* ignore */ }
  }

  let storedInitial = false;
  try { storedInitial = sessionStorage.getItem(STORAGE_KEY) === 'true'; } catch (_) { /* ignore */ }
  if (TEMP_OPEN_ACCESS) {
    console.log('[ACCESS][TEMP_BYPASS_ACTIVE]', true);
  } else {
    console.log('[ACCESS][LOCAL_EXISTS]', storedInitial);
  }

  // Estados:
  //   'allowed'  → mostrar ruleta
  //   'blocked'  → mostrar bloqueo
  //   'checking' → tenemos access_token y estamos validando
  const [status, setStatus] = useState(() => {
    if (TEMP_OPEN_ACCESS) return 'allowed';
    if (storedInitial) return 'allowed';
    if (accessToken) return 'checking';
    console.log('[ACCESS][BLOCK_REASON]', 'no_token_and_no_local');
    return 'blocked';
  });

  useEffect(() => {
    if (TEMP_OPEN_ACCESS) return;
    if (status !== 'checking' || !accessToken) return;
    let cancelled = false;
    validateAccessToken(accessToken).then((data) => {
      if (cancelled) return;
      if (data && data.valid === true) {
        try {
          sessionStorage.setItem(STORAGE_KEY, 'true');
          console.log('[ACCESS][SESSION_SET]', true);
        } catch (_) { /* ignore */ }
        // Limpia el access_token de la URL sin recargar.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('access_token');
          const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash;
          window.history.replaceState({}, '', cleaned);
        } catch (_) { /* ignore */ }
        console.log('[ACCESS][ALLOW]');
        setStatus('allowed');
      } else {
        console.log('[ACCESS][BLOCK_REASON]', 'validate_returned_invalid', data);
        setStatus('blocked');
      }
    });
    return () => { cancelled = true; };
  }, [status, accessToken]);

  if (status === 'allowed') {
    return <Ruleta />;
  }

  if (status === 'checking') {
    return (
      <div className="ruleta-gate-screen" data-testid="ruleta-gate-checking">
        <div className="ruleta-gate-card">
          <div className="ruleta-gate-icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>
          <h1 className="ruleta-gate-title">Verificando acceso…</h1>
          <p className="ruleta-gate-subtitle">Un momento, por favor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ruleta-gate-screen" data-testid="ruleta-gate-blocked">
      <div className="ruleta-gate-card">
        <div className="ruleta-gate-icon" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
        </div>
        <h1 className="ruleta-gate-title">Abre la ruleta desde la app Giro Expréss</h1>
        <p className="ruleta-gate-subtitle">
          Esta dinámica está disponible solo desde la aplicación oficial.
        </p>
      </div>
    </div>
  );
}
